/**
 * Route configuration with page titles and navigation metadata
 */

interface RouteConfig {
  key: string
  label: string
  path: string
  icon?: null
  parentKey?: string
}

export const routes = {
  // The portfolio calendar: every property on one timeline (CAP-1415). It
  // replaced the dashboard at `/`; `/dashboard` redirects here.
  calendar: {
    key: 'calendar',
    label: 'Calendar',
    path: '/',
    icon: null,
  },
  inbox: {
    key: 'inbox',
    label: 'Inbox',
    path: '/inbox',
    icon: null,
  },
  inboxThread: {
    key: 'inboxThread',
    label: 'Inbox',
    path: '/inbox/:threadId',
    parentKey: 'inbox',
  },
  properties: {
    key: 'properties',
    label: 'Properties',
    path: '/properties',
    icon: null, // Can add icon if needed
  },
  users: {
    key: 'users',
    label: 'Users',
    path: '/users',
    icon: null,
  },
  userDetails: {
    key: 'userDetails',
    label: 'User',
    path: '/users/:userId',
    parentKey: 'users',
  },
  assets: {
    key: 'assets',
    label: 'Assets',
    path: '/assets',
    icon: null,
  },
  operations: {
    key: 'operations',
    label: 'Settings',
    path: '/operations',
    icon: null, // Can add icon if needed
  },
  newsletters: {
    key: 'newsletters',
    label: 'Newsletters',
    path: '/newsletters',
    icon: null,
  },
  protection: {
    key: 'protection',
    label: 'Protection',
    path: '/protection',
    icon: null,
  },
  reviews: {
    key: 'reviews',
    label: 'Reviews',
    path: '/reviews',
    icon: null,
  },
  orphanOffers: {
    key: 'orphanOffers',
    label: 'Orphan Nights',
    path: '/orphan-offers',
    icon: null,
  },
  taxReports: {
    key: 'taxReports',
    label: 'Tax reports',
    path: '/tax-reports',
    icon: null,
  },
  cleaning: {
    key: 'cleaning',
    label: 'Cleaning',
    path: '/cleaning',
    icon: null,
  },
  supplies: {
    key: 'supplies',
    label: 'Supplies',
    path: '/supplies',
    icon: null,
  },
  propertyDetails: {
    key: 'propertyDetails',
    label: 'Property Details',
    path: '/properties/:propertyId',
    parentKey: 'properties',
  },
  reservationDetails: {
    key: 'reservationDetails',
    label: 'Reservation Details',
    path: '/reservations/:reservationId',
    parentKey: 'properties',
  },
  threadDetails: {
    key: 'threadDetails',
    label: 'Thread Details',
    path: '/threads/:threadId',
    parentKey: 'properties',
  },
  propertyMessages: {
    key: 'propertyMessages',
    label: 'Messages',
    path: '/properties/:propertyId/messages',
    parentKey: 'properties',
  },
} satisfies Record<string, RouteConfig>

/**
 * Navigation items for topbar/sidebar
 * Uses routes config as single source of truth for labels
 */
export const navigation = [
  {
    name: routes.calendar.label,
    href: routes.calendar.path,
    key: routes.calendar.key,
    icon: null,
    external: false,
  },
  {
    name: routes.inbox.label,
    href: routes.inbox.path,
    key: routes.inbox.key,
    icon: null,
    external: false,
  },
  {
    name: routes.properties.label,
    href: routes.properties.path,
    key: routes.properties.key,
    icon: null, // Icon component will be passed from component
    external: false,
  },
  {
    name: routes.users.label,
    href: routes.users.path,
    key: routes.users.key,
    icon: null,
    external: false,
  },
  {
    name: routes.assets.label,
    href: routes.assets.path,
    key: routes.assets.key,
    icon: null,
    external: false,
  },
  {
    name: routes.operations.label,
    href: routes.operations.path,
    key: routes.operations.key,
    icon: null, // Icon component will be passed from component
    external: false,
  },
  {
    name: routes.newsletters.label,
    href: routes.newsletters.path,
    key: routes.newsletters.key,
    icon: null,
    external: false,
  },
  {
    name: routes.protection.label,
    href: routes.protection.path,
    key: routes.protection.key,
    icon: null,
    external: false,
  },
  {
    name: routes.reviews.label,
    href: routes.reviews.path,
    key: routes.reviews.key,
    icon: null,
    external: false,
  },
  {
    name: routes.orphanOffers.label,
    href: routes.orphanOffers.path,
    key: routes.orphanOffers.key,
    icon: null,
    external: false,
  },
  {
    name: routes.taxReports.label,
    href: routes.taxReports.path,
    key: routes.taxReports.key,
    icon: null,
    external: false,
  },
  {
    name: routes.cleaning.label,
    href: routes.cleaning.path,
    key: routes.cleaning.key,
    icon: null,
    external: false,
  },
  {
    name: routes.supplies.label,
    href: routes.supplies.path,
    key: routes.supplies.key,
    icon: null,
    external: false,
  },
  {
    name: 'Uplisting',
    href: 'https://app.uplisting.io/calendar',
    key: 'uplisting',
    icon: null, // Icon component will be passed from component
    external: true,
  },
  {
    name: 'Clearing',
    href: 'https://app.getclearing.co/home?Section=Transactions',
    key: 'clearing',
    icon: null, // Icon component will be passed from component
    external: true,
  },
]

/**
 * Whether a sidebar entry is the one the page is under: its own path or a page
 * below it. The calendar lives at `/`, which every path starts with, so it is
 * active on `/` alone.
 */
export function isNavItemActive(pathname: string, href: string): boolean {
  return href === '/' ? pathname === '/' : pathname.startsWith(href)
}

/**
 * Get route config by pathname
 */
export function getRouteByPathname(pathname: string): RouteConfig {
  if (pathname === '/') {
    return routes.calendar
  }
  if (pathname === '/inbox') {
    return routes.inbox
  }
  if (pathname.startsWith('/inbox/')) {
    return routes.inboxThread
  }
  // Match exact or starts with pattern
  if (pathname === '/properties' || pathname.startsWith('/properties/')) {
    if (pathname === '/properties') {
      return routes.properties
    }
    if (pathname.match(/^\/properties\/[^/]+\/messages(\/[^/]+)?$/)) {
      return routes.propertyMessages
    }
    if (pathname.match(/^\/properties\/[^/]+$/)) {
      return routes.propertyDetails
    }
  }
  if (pathname === '/operations') {
    return routes.operations
  }
  if (pathname === '/newsletters') {
    return routes.newsletters
  }
  if (pathname === '/protection') {
    return routes.protection
  }
  if (pathname === '/reviews') {
    return routes.reviews
  }
  if (pathname === '/orphan-offers') {
    return routes.orphanOffers
  }
  if (pathname === '/cleaning') {
    return routes.cleaning
  }
  if (pathname === '/tax-reports') {
    return routes.taxReports
  }
  if (pathname === '/supplies') {
    return routes.supplies
  }
  if (pathname.startsWith('/users/')) {
    return routes.userDetails
  }
  if (pathname === '/users') {
    return routes.users
  }
  if (pathname === '/assets') {
    return routes.assets
  }
  if (pathname.startsWith('/reservations/')) {
    return routes.reservationDetails
  }
  if (pathname.startsWith('/threads/')) {
    return routes.threadDetails
  }
  // Default to properties
  return routes.properties
}

/**
 * Get page title from pathname
 */
export function getPageTitle(pathname: string): string {
  const route = getRouteByPathname(pathname)
  // If it's a detail page, prefer showing the parent label (Properties)
  // Otherwise show the route label
  return route.parentKey ? routes[route.parentKey as keyof typeof routes].label : route.label
}
